const CACHE_VERSION = '1.0';
const CACHE_PREFIX = 'wtc_team_admin_';

export class CacheService {
  constructor() {
    this.initializeCache();
  }

  initializeCache() {
    try {
      const versionKey = `${CACHE_PREFIX}version`;
      const currentVersion = localStorage.getItem(versionKey);
      
      if (currentVersion !== CACHE_VERSION) {
        this.clearAll();
        localStorage.setItem(versionKey, CACHE_VERSION);
      }
    } catch (error) {
      console.error('Failed to initialize cache:', error);
    }
  }

  set(key, value, expiresInMinutes = null) {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cacheData = {
      value,
      timestamp: Date.now(),
      expiresAt: expiresInMinutes ? Date.now() + (expiresInMinutes * 60 * 1000) : null
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('Failed to set cache:', error);
      if (error.name === 'QuotaExceededError') {
        this.clearOldest();
        try {
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          return true;
        } catch (retryError) {
          console.error('Failed to set cache after clearing:', retryError);
        }
      }
      return false;
    }
  }

  get(key) {
    try {
      const cacheKey = `${CACHE_PREFIX}${key}`;
      const cacheData = localStorage.getItem(cacheKey);
      
      if (!cacheData) {
        return null;
      }
      
      const parsed = JSON.parse(cacheData);
      
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.error('Failed to get cache:', error);
      return null;
    }
  }

  getValue(key) {
    const cacheData = this.get(key);
    return cacheData ? cacheData.value : null;
  }

  getWithMetadata(key) {
    return this.get(key);
  }

  remove(key) {
    try {
      const cacheKey = `${CACHE_PREFIX}${key}`;
      localStorage.removeItem(cacheKey);
      return true;
    } catch (error) {
      console.error('Failed to remove cache:', error);
      return false;
    }
  }

  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error('Failed to clear cache:', error);
      return false;
    }
  }

  clearOldest() {
    try {
      const cacheItems = [];
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX) && key !== `${CACHE_PREFIX}version`) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            cacheItems.push({ key, timestamp: data.timestamp || 0 });
          } catch (_error) {
            // Invalid cache item, remove it
            localStorage.removeItem(key);
          }
        }
      });
      
      // Sort by timestamp (oldest first)
      cacheItems.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 25% of items
      const removeCount = Math.max(1, Math.floor(cacheItems.length * 0.25));
      for (let i = 0; i < removeCount; i++) {
        localStorage.removeItem(cacheItems[i].key);
      }
    } catch (error) {
      console.error('Failed to clear oldest cache items:', error);
    }
  }

  getCacheSize() {
    let size = 0;
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        const item = localStorage.getItem(key);
        size += item ? item.length : 0;
      }
    });
    
    return size;
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return 'Unknown';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  }
}

export const cacheService = new CacheService();